import { makeStyles, tokens } from '@fluentui/react-components';
export const EXPANDED_WIDTH = '272px';
export const COLLAPSED_WIDTH = '68px';
const focusRing = {
    outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
    outlineOffset: '-2px',
};
const reducedMotion = {
    '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0.01ms' },
};
export const useSidebarStyles = makeStyles({
    // Envolve a barra para a alça ficar sobre a borda, fora do recorte dela.
    wrapper: {
        position: 'relative',
        display: 'flex',
        flexShrink: 0,
        height: '100%',
    },
    // Alça de recolher: sobre a borda direita, na altura da marca.
    handle: {
        position: 'absolute',
        top: '24px',
        right: '-12px',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
        padding: 0,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: tokens.colorNeutralBackground1,
        boxShadow: tokens.shadow4,
        color: tokens.colorNeutralForeground2,
        fontSize: '14px',
        cursor: 'pointer',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: tokens.colorBrandForeground1,
            border: `1px solid ${tokens.colorBrandStroke1}`,
        },
        ':focus-visible': { ...focusRing, outlineOffset: '2px' },
    },
    arrow: {
        transitionProperty: 'transform',
        transitionDuration: tokens.durationNormal,
        ...reducedMotion,
    },
    arrowCollapsed: { transform: 'rotate(180deg)' },
    close: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        width: EXPANDED_WIDTH,
        height: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRight: `1px solid ${tokens.colorNeutralStroke3}`,
        transitionProperty: 'width',
        transitionDuration: tokens.durationNormal,
        transitionTimingFunction: tokens.curveEasyEase,
        ...reducedMotion,
    },
    rootCollapsed: { width: COLLAPSED_WIDTH },
    // Na gaveta, a barra ocupa o painel inteiro.
    rootDrawer: { width: '100%', borderRight: 'none' },
    drawerBody: { padding: 0 },
    // Cabeçalho: mesma altura e posição da marca nos dois estados.
    header: {
        display: 'flex',
        alignItems: 'center',
        height: '72px',
        flexShrink: 0,
        padding: '0 10px',
        boxSizing: 'border-box',
        borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    },
    brand: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        flexGrow: 1,
        minWidth: 0,
        padding: tokens.spacingHorizontalXS,
        border: 'none',
        borderRadius: tokens.borderRadiusXLarge,
        backgroundColor: 'transparent',
        color: 'inherit',
        font: 'inherit',
        textAlign: 'start',
        cursor: 'pointer',
        ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
        ':focus-visible': focusRing,
    },
    brandTile: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        flexShrink: 0,
        borderRadius: tokens.borderRadiusXLarge,
        backgroundImage: `linear-gradient(135deg, ${tokens.colorBrandBackground2} 0%, ${tokens.colorBrandBackground2Pressed} 100%)`,
    },
    brandLogo: { display: 'block', width: '26px', height: '26px' },
    brandText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
    brandName: {
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    subtle: { color: tokens.colorNeutralForeground3 },
    scroll: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        flexGrow: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalL}`,
        scrollbarWidth: 'thin',
        scrollbarColor: `${tokens.colorNeutralStroke1} transparent`,
    },
    // Rótulo de seção. Altura fixa: recolhida, a barra mostra um traço no
    // mesmo espaço, para os ícones não mudarem de posição.
    section: {
        display: 'flex',
        alignItems: 'center',
        height: '28px',
        flexShrink: 0,
        padding: `0 ${tokens.spacingHorizontalM}`,
        color: tokens.colorNeutralForeground3,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        whiteSpace: 'nowrap',
    },
    sectionDash: {
        width: '20px',
        height: '1px',
        backgroundColor: tokens.colorNeutralStroke2,
    },
    item: {
        position: 'relative',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        width: '100%',
        minHeight: '40px',
        padding: `0 ${tokens.spacingHorizontalM}`,
        boxSizing: 'border-box',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: tokens.borderRadiusXLarge,
        color: tokens.colorNeutralForeground2,
        fontFamily: 'inherit',
        fontSize: tokens.fontSizeBase300,
        textAlign: 'start',
        cursor: 'pointer',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: tokens.colorNeutralForeground1,
        },
        ':focus-visible': focusRing,
    },
    itemCollapsed: { width: '44px' },
    groupHeader: {
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightSemibold,
    },
    groupActive: { color: tokens.colorBrandForeground1 },
    // Alinha o texto do subitem com o título do módulo (ícone + espaço).
    subItem: {
        minHeight: '36px',
        paddingLeft: `calc(${tokens.spacingHorizontalM} + 20px + ${tokens.spacingHorizontalM})`,
    },
    selected: {
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
        fontWeight: tokens.fontWeightSemibold,
        ':hover': {
            backgroundColor: tokens.colorBrandBackground2Hover,
            color: tokens.colorBrandForeground1,
        },
    },
    icon: { display: 'flex', flexShrink: 0, fontSize: '20px' },
    label: {
        flexGrow: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    chevron: {
        flexShrink: 0,
        color: tokens.colorNeutralForeground3,
        transitionProperty: 'transform',
        transitionDuration: tokens.durationFast,
        ...reducedMotion,
    },
    chevronClosed: { transform: 'rotate(-90deg)' },
    subList: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
        listStyleType: 'none',
        margin: `${tokens.spacingVerticalXXS} 0 ${tokens.spacingVerticalXS}`,
        padding: 0,
    },
    footer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        marginTop: 'auto',
        paddingTop: tokens.spacingVerticalM,
        borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    },
});
//# sourceMappingURL=sidebar.styles.js.map